import { useUserData } from '@flik/shared';
import { useReactQuery } from '@flik/shared/api';
import { Room, RoomEvent, Track } from 'livekit-client';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseLivekitArgs {
  sessionId: string | null;
  livekitUrl: string | null;
  isMicOn?: boolean;
  isVideoOn?: boolean;
  isScreenSharing?: boolean;
  isSystemSoundOn?: boolean;
}

interface UserIdentity {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export function useLivekit({
  sessionId,
  livekitUrl,
  isMicOn = false,
  isVideoOn = false,
  isScreenSharing = false,
  isSystemSoundOn = false,
}: UseLivekitArgs) {
  const roomRef = useRef<Room | null>(null);
  const [isLivekitConnected, setIsLivekitConnected] = useState(false);
  const [participants, setParticipants] = useState<Map<string, any>>(new Map());
  const [localParticipant, setLocalParticipant] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isMicOnState, setIsMicOnState] = useState(isMicOn);
  const [isVideoOnState, setIsVideoOnState] = useState(isVideoOn);
  const [focusedTrack, setFocusedTrack] = useState<any>(null);
  const [trackUpdateCounter, setTrackUpdateCounter] = useState(0);
  const [visibleParticipants, setVisibleParticipants] = useState<any[]>([]);
  const [nonCameraVideoTracks, setNonCameraVideoTracks] = useState<any[]>([]);
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  const nonCameraVideoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const audioRefs = useRef<Map<string, HTMLAudioElement>>(new Map());

  const apiClient = useReactQuery();
  const {
    userData,
    isLoading: isUserDataLoading,
    isError: isUserDataError,
  } = useUserData();

  // Create the mutation hook
  const fetchLivekitToken = apiClient.useMutation('post', '/v1/livekit/token');

  // Track if we're currently connecting to prevent multiple simultaneous connections
  const isConnectingRef = useRef(false);

  // Effect 1: Manage room connect/disconnect (only on URL/token change)
  useEffect(() => {
    let isCancelled = false;

    async function doConnect() {
      if (!sessionId || !livekitUrl) {
        return;
      }

      // Wait for user identity to be fetched
      if (isUserDataLoading) {
        return;
      }

      if (isUserDataError || !userData) {
        return;
      }

      // Prevent multiple simultaneous connections
      if (isConnectingRef.current) {
        return;
      }

      isConnectingRef.current = true;

      const userIdentity: UserIdentity = {
        id: userData.id || '',
        firstName: userData.first_name || '',
        lastName: userData.last_name || '',
        email: userData.email || '',
      };

      // Disconnect existing room if any
      if (roomRef.current) {
        try {
          await roomRef.current.disconnect();
        } catch (e) {
          // Ignore disconnect errors
        }
        roomRef.current = null;
        setIsLivekitConnected(false);
        setParticipants(new Map());
        setLocalParticipant(null);
        setError(null);
        setIsMicOnState(false);
        setIsVideoOnState(false);
        setFocusedTrack(null);
        setTrackUpdateCounter(0);
      }

      try {
        // Fetch LiveKit token using the user identity
        const identity = userIdentity.id;
        const name =
          `${userIdentity.firstName} ${userIdentity.lastName}`.trim() ||
          'Web User';

        const tokenResult = await fetchLivekitToken.mutateAsync({
          body: {
            identity,
            name,
            session: sessionId,
          },
        });

        if (isCancelled) {
          return;
        }

        const token = tokenResult.token;

        if (!token) {
          throw new Error('No token received from API');
        }

        // Create and connect to room
        const room = new Room({
          adaptiveStream: true,
          dynacast: true,
        });
        roomRef.current = room;

        // Set up event listeners
        room.on(RoomEvent.Connected, () => {
          if (isCancelled) return;
          setIsLivekitConnected(true);
          setLocalParticipant(room.localParticipant);
          setError(null);

          // Add existing participants
          const existingParticipants = new Map();
          room.remoteParticipants.forEach((participant) => {
            existingParticipants.set(participant.identity, participant);
          });
          setParticipants(existingParticipants);
        });

        room.on(RoomEvent.Disconnected, () => {
          if (isCancelled) return;
          setIsLivekitConnected(false);
          setLocalParticipant(null);
          setParticipants(new Map());
          setIsMicOnState(false);
          setIsVideoOnState(false);
          setFocusedTrack(null);
          // Clean up audio elements
          audioRefs.current.forEach((audioElement) => {
            audioElement.remove();
          });
          audioRefs.current.clear();
          roomRef.current = null;
        });

        room.on(RoomEvent.ParticipantConnected, (participant) => {
          if (isCancelled) return;
          setParticipants((prev) => {
            const newMap = new Map(prev);
            newMap.set(participant.identity, participant);
            return newMap;
          });
        });

        room.on(RoomEvent.ParticipantDisconnected, (participant) => {
          if (isCancelled) return;
          setParticipants((prev) => {
            const newMap = new Map(prev);
            newMap.delete(participant.identity);
            return newMap;
          });
        });

        // Track publication events for screen sharing
        room.on(RoomEvent.TrackPublished, (publication, participant) => {
          if (isCancelled) return;
          console.log(
            'Track published:',
            publication.source,
            publication.kind,
            participant.identity,
          );
          // Only update participants for screen share tracks, not camera tracks
          if (publication.source === Track.Source.ScreenShare) {
            setParticipants((prev) => {
              const newMap = new Map(prev);
              newMap.set(participant.identity, participant);
              return newMap;
            });
            setTrackUpdateCounter((prev) => prev + 1);
          }
        });

        room.on(RoomEvent.TrackUnpublished, (publication, participant) => {
          if (isCancelled) return;
          console.log(
            'Track unpublished:',
            publication.source,
            publication.kind,
            participant.identity,
          );
          // Only update participants for screen share tracks, not camera tracks
          if (publication.source === Track.Source.ScreenShare) {
            setParticipants((prev) => {
              const newMap = new Map(prev);
              newMap.set(participant.identity, participant);
              return newMap;
            });
            setTrackUpdateCounter((prev) => prev + 1);
          }
        });

        room.on(
          RoomEvent.TrackSubscribed,
          (track, publication, participant) => {
            if (isCancelled) return;
            console.log(
              'Track subscribed:',
              publication.source,
              publication.kind,
              participant.identity,
            );
            // Update participants to include the subscribed track
            setParticipants((prev) => {
              const newMap = new Map(prev);
              newMap.set(participant.identity, participant);
              return newMap;
            });
            setTrackUpdateCounter((prev) => prev + 1);
          },
        );

        room.on(
          RoomEvent.TrackUnsubscribed,
          (track, publication, participant) => {
            if (isCancelled) return;
            console.log(
              'Track unsubscribed:',
              publication.source,
              publication.kind,
              participant.identity,
            );
            // Update participants to reflect the unsubscribed track
            setParticipants((prev) => {
              const newMap = new Map(prev);
              newMap.set(participant.identity, participant);
              return newMap;
            });
            setTrackUpdateCounter((prev) => prev + 1);
          },
        );

        // Listen for track muting/unmuting events
        room.on(RoomEvent.TrackMuted, (publication, participant) => {
          if (isCancelled) return;
          console.log(
            'Track muted:',
            publication.source,
            publication.kind,
            participant.identity,
          );
          // Update participants when tracks are muted (camera/mic off)
          setParticipants((prev) => {
            const newMap = new Map(prev);
            newMap.set(participant.identity, participant);
            return newMap;
          });
          setTrackUpdateCounter((prev) => prev + 1);
        });

        room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
          if (isCancelled) return;
          console.log(
            'Track unmuted:',
            publication.source,
            publication.kind,
            participant.identity,
          );
          // Update participants when tracks are unmuted (camera/mic on)
          setParticipants((prev) => {
            const newMap = new Map(prev);
            newMap.set(participant.identity, participant);
            return newMap;
          });
          setTrackUpdateCounter((prev) => prev + 1);
        });

        // Handle remote audio track subscription/unsubscription
        room.on(
          RoomEvent.TrackSubscribed,
          (track, _publication, participant) => {
            if (isCancelled) return;
            if (track.kind === Track.Kind.Audio) {
              console.log('Audio track subscribed:', participant.identity);
              // Attach audio track to a hidden audio element
              const audioElement = track.attach() as HTMLAudioElement;
              audioElement.autoplay = true;
              audioElement.style.display = 'none';
              audioRefs.current.set(participant.identity, audioElement);
              document.body.appendChild(audioElement);
            }
          },
        );

        room.on(
          RoomEvent.TrackUnsubscribed,
          (track, _publication, participant) => {
            if (isCancelled) return;
            if (track.kind === Track.Kind.Audio) {
              console.log('Audio track unsubscribed:', participant.identity);
              // Detach and remove audio element
              track.detach();
              const audioElement = audioRefs.current.get(participant.identity);
              if (audioElement) {
                audioElement.remove();
                audioRefs.current.delete(participant.identity);
              }
            }
          },
        );

        await room.connect(livekitUrl, token);

        if (isCancelled) {
          await room.disconnect();
          return;
        }

        // Apply initial video and microphone states after connection
        if (isVideoOn) {
          await room.localParticipant.setCameraEnabled(true);
        }
        if (isMicOn) {
          await room.localParticipant.setMicrophoneEnabled(true);
        }
      } catch (err) {
        console.error('[Web] Failed to connect to LiveKit:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to connect to LiveKit',
        );
        setIsLivekitConnected(false);
        roomRef.current = null;
      } finally {
        isConnectingRef.current = false;
      }
    }

    doConnect();

    return () => {
      isCancelled = true;
      isConnectingRef.current = false;
    };
  }, [sessionId, livekitUrl, userData, isUserDataLoading, isUserDataError]);

  // Effect 2: Update visible participants and non-camera video tracks
  useEffect(() => {
    const currentRoom = roomRef.current;
    if (!currentRoom || !isLivekitConnected) {
      setVisibleParticipants([]);
      setNonCameraVideoTracks([]);
      return;
    }

    const updateParticipants = () => {
      // Get all participants from the room (including local participant)
      const allParticipants = [
        currentRoom.localParticipant,
        ...Array.from(currentRoom.remoteParticipants.values()),
      ];

      // Only update visibleParticipants if the participant list actually changed
      const currentIdentities = allParticipants.map((p) => p.identity).sort();
      const prevIdentities = visibleParticipants.map((p) => p.identity).sort();

      if (
        currentIdentities.length !== prevIdentities.length ||
        !currentIdentities.every((id, index) => id === prevIdentities[index])
      ) {
        setVisibleParticipants(allParticipants);
      }

      // Collect non-camera video tracks (screen shares, etc.)
      const nonCameraTracks: any[] = [];
      allParticipants.forEach((participant) => {
        participant.trackPublications.forEach((publication: any) => {
          if (
            publication.source === Track.Source.ScreenShare &&
            publication.kind === Track.Kind.Video &&
            publication.isSubscribed &&
            !publication.isMuted &&
            publication.track
          ) {
            nonCameraTracks.push({
              participantIdentity: participant.identity,
              trackSid: publication.trackSid,
              publication,
            });
          }
        });
      });
      setNonCameraVideoTracks(nonCameraTracks);
    };

    updateParticipants();
  }, [participants, isLivekitConnected, trackUpdateCounter]);

  // Media control functions
  const toggleMicrophone = useCallback(async () => {
    const currentRoom = roomRef.current;
    if (!currentRoom) return;

    try {
      if (isMicOnState) {
        await currentRoom.localParticipant.setMicrophoneEnabled(false);
        setIsMicOnState(false);
      } else {
        // Enable microphone in LiveKit - the MicrophoneManager will handle stream creation
        await currentRoom.localParticipant.setMicrophoneEnabled(true);
        setIsMicOnState(true);
      }
    } catch (err) {
      console.error('Failed to toggle microphone:', err);
    }
  }, [isMicOnState]);

  const toggleVideo = useCallback(async () => {
    const currentRoom = roomRef.current;
    if (!currentRoom) return;

    try {
      if (isVideoOnState) {
        await currentRoom.localParticipant.setCameraEnabled(false);
        setIsVideoOnState(false);
      } else {
        // Enable camera in LiveKit - the CameraManager will handle stream creation
        await currentRoom.localParticipant.setCameraEnabled(true);
        setIsVideoOnState(true);
      }
    } catch (err) {
      console.error('Failed to toggle video:', err);
    }
  }, [isVideoOnState]);

  const handleExitFocusMode = useCallback(() => {
    setFocusedTrack(null);
  }, []);

  const registerVideoElement = useCallback(
    (
      identity: string,
      element: HTMLVideoElement | null,
      isLocal: boolean,
      trackSid?: string,
    ) => {
      const currentRoom = roomRef.current;
      if (!currentRoom) return;

      if (isLocal) {
        localVideoRef.current = element;
        // Handle local video track
        if (element) {
          const localVideoTrack =
            currentRoom.localParticipant.videoTrackPublications.get(
              Track.Source.Camera,
            );
          if (
            localVideoTrack?.track &&
            !localVideoTrack.track.attachedElements.includes(element)
          ) {
            localVideoTrack.track.attach(element);
          }
        }
      } else {
        // Update refs directly without state
        if (element) {
          videoRefs.current.set(identity, element);
        } else {
          videoRefs.current.delete(identity);
        }

        // Handle remote video track attachment/detachment
        const participant = currentRoom.remoteParticipants.get(identity);
        if (participant) {
          if (trackSid) {
            // For screen shares, find the specific track by trackSid
            const trackPub = Array.from(
              participant.trackPublications.values(),
            ).find((pub: any) => pub.trackSid === trackSid);
            if (trackPub?.track) {
              if (
                element &&
                !trackPub.track.attachedElements.includes(element)
              ) {
                trackPub.track.attach(element);
              } else if (!element) {
                trackPub.track.detach();
              }
            }
          } else {
            // For camera video, find the camera track
            const videoTrack = participant.videoTrackPublications.get(
              Track.Source.Camera,
            );
            if (videoTrack?.track) {
              if (
                element &&
                !videoTrack.track.attachedElements.includes(element)
              ) {
                videoTrack.track.attach(element);
              } else if (!element) {
                videoTrack.track.detach();
              }
            }
          }
        }
      }
    },
    [roomRef],
  );

  const disconnectFromLivekit = useCallback(() => {
    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current = null;
      setIsLivekitConnected(false);
    }
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }
      // Clean up audio elements
      audioRefs.current.forEach((audioElement) => {
        audioElement.remove();
      });
      audioRefs.current.clear();
    };
  }, []);

  return {
    roomRef,
    isLivekitConnected,
    error,
    disconnectFromLivekit,
    isMicOn: isMicOnState,
    isVideoOn: isVideoOnState,
    nonCameraVideoTracks,
    visibleParticipants,
    focusedTrack,
    videoRefs,
    nonCameraVideoRefs,
    localVideoRef,
    audioRefs,
    toggleMicrophone,
    toggleVideo,
    handleExitFocusMode,
    setFocusedTrack,
    registerVideoElement,
    userIdentity: userData
      ? {
          id: userData.id || '',
          firstName: userData.first_name || '',
          lastName: userData.last_name || '',
          email: userData.email || '',
        }
      : null,
  };
}
